import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../redux/store';
import {
  upsertShortstay,
  listenToShortstayData,
} from '../../../../redux/shortstaysSlice';

// ✅ Icons
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import BedIcon from '@mui/icons-material/Bed';
import GroupIcon from '@mui/icons-material/Group';

const OPTIONS = [
  {
    id: 'entire_place',
    title: 'An entire place',
    description: 'Guests have the whole place to themselves.',
    icon: <HomeWorkIcon sx={{ fontSize: 40 }} />,
  },
  {
    id: 'room',
    title: 'A room',
    description: 'Guests have their own room in a home, plus access to shared spaces.',
    icon: <BedIcon sx={{ fontSize: 40 }} />,
  },
  {
    id: 'shared_room',
    title: 'A shared room in a hostel',
    description:
      'Guests sleep in a shared room in a professionally managed hostel with staff onsite 24/7.',
    icon: <GroupIcon sx={{ fontSize: 40 }} />,
  },
];

const ShortForm2: React.FC = () => {
  const { userId, listingId } = useParams<{ userId: string; listingId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const currentListing = useSelector((state: any) => state.shortstays.currentListing);

  const [placeType, setPlaceType] = useState<string>('');

  useEffect(() => {
    if (listingId) {
      dispatch(listenToShortstayData(listingId));
    }
  }, [listingId, dispatch]);

  useEffect(() => {
    if (currentListing?.placeType) {
      setPlaceType(currentListing.placeType);
    }
  }, [currentListing]);

  const handleSelect = (id: string) => setPlaceType(id);

  const handleNext = async () => {
    if (!listingId || !placeType) return;
    await dispatch(upsertShortstay({ listingId, data: { placeType } }));
    navigate(`/shortform3/${userId}/${listingId}`);
  };

  const handleBack = () => {
    navigate(`/shortform1/${userId}`);
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>What type of place will guests have?</h2>

      <div style={styles.optionGrid}>
        {OPTIONS.map((opt) => {
          const selected = placeType === opt.id;
          return (
            <div
              key={opt.id}
              onClick={() => handleSelect(opt.id)}
              style={{
                ...styles.card,
                background: selected
                  ? 'linear-gradient(to right, #111, #bf9b30)'
                  : '#fff',
                color: selected ? '#fff' : '#000',
                border: selected ? '2px solid #bf9b30' : '1px solid #ccc',
                transform: selected ? 'scale(1.03)' : 'scale(1)',
              }}
            >
              <div
                style={{
                  color: selected ? '#fff' : '#bf9b30',
                  marginBottom: 10,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {opt.icon}
              </div>
              <h3 style={styles.cardTitle}>{opt.title}</h3>
              <p style={styles.cardDesc}>{opt.description}</p>
            </div>
          );
        })}
      </div>

      <footer style={styles.footer}>
        <button onClick={handleBack} style={styles.backBtn}>
          Back
        </button>
        <button
          onClick={handleNext}
          style={{
            ...styles.nextBtn,
            opacity: placeType ? 1 : 0.5,
            cursor: placeType ? 'pointer' : 'not-allowed',
          }}
          disabled={!placeType}
        >
          Continue
        </button>
      </footer>
    </div>
  );
};

export default ShortForm2;

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: '#000',
    color: '#fff',
    padding: 20,
    paddingBottom: 100,
    minHeight: '100vh',
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 30,
    textAlign: 'center',
    color: '#bf9b30',
  },
  optionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 20,
    marginBottom: 40,
  },
  card: {
    borderRadius: 16,
    padding: 20,
    cursor: 'pointer',
    boxShadow: '0 0 10px rgba(255,255,255,0.1)',
    transition: 'all 0.3s ease',
    textAlign: 'center',
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  cardDesc: {
    fontSize: 14,
    color: '#ccc',
  },
  footer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#000',
    borderTop: '1px solid #333',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '15px 30px',
    zIndex: 1000,
  },
  backBtn: {
    padding: '10px 20px',
    borderRadius: 30,
    backgroundColor: '#222',
    color: '#fff',
    border: '1px solid #555',
    cursor: 'pointer',
  },
  nextBtn: {
    padding: '10px 20px',
    borderRadius: 30,
    background: 'linear-gradient(to right, #fff, #bf9b30)',
    color: '#000',
    border: 'none',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
};
